import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Link,
  Checkbox,
  Tabs,
  Tab
} from '@mui/material';
import { 
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  Email,
  Edit,
  Delete,
  DesktopWindows,
  Language,
  Send,
  NavigateNext,
  Print,
  EventAvailable,
  FilterAlt,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {  
  useGetWeeklyShiftsByWardQuery,
  useAssignStaffToShiftMutation,
  useUpdateShiftMutation,
  useReOpenShiftMutation ,
  useCreateShiftMutation // Add this import
} from '../../../slices/shiftApiSlice';


import { useGetUsersQuery } from '../../../slices/usersApiSlice';
import { useSelector } from 'react-redux';

// Add import for availability API
import { useGetWeeklyAvailabilitiesByStaffQuery } from '../../../slices/availabilityApiSlice';

// Add import for hospital locations
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';

// Import enums for consistency
import {
  CATEGORY_OF_STAFF,
  REASON_FOR_REQUEST,
  SHIFT_STATUS,
  LINE_STATUS,
} from '../../../utils/enums';

// Add SHIFT_PRESETS constant
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

const UNAVAILABILITY_REASONS = [
  'Sick Leave',
  'Maternity Leave',
  'Bereavement Leave',
  'Study Leave / Mandatory Training',
  'Force Majeure',
  'Paternity Leave',
  'Parent Leave',
  'Jury Leave',
  'Other'
];

// Add these style objects near the top of your component (around line 40-50)
const scrollableTableStyle = {
  maxHeight: '70vh', // Adjust height as needed - 70% of viewport height
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
};

const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Print styles (keep existing code)
const PrintStyles = () => (
  <style>{`
    @media print {
      /* Hide non-essential elements */
      .no-print {
        display: none !important;
      }
      
      /* Hide availability section in print */
      .availability-section {
        display: none !important;
      }
      
      /* Ensure proper page breaking */
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      
      /* Ensure colors are preserved in print */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      
      /* Fix for Firefox background colors */
      @-moz-document url-prefix() {
        .shift-card, .ward-card, .staff-card, .header-cell {
          box-shadow: inset 0 0 0 1000px var(--print-color) !important;
        }
      }
      
      /* Print-specific adjustments */
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }
      
      .print-container {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Ensure calendar table prints properly */
      .calendar-table {
        width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
      }
      
      /* Make text black for better readability in print */
      .shift-text, .ward-text {
        color: #000 !important;
        font-weight: bold !important;
      }
      
      /* Larger text for print */
      .print-text {
        font-size: 11pt !important;
        line-height: 1.4 !important;
      }
      
      /* Page margins */
      @page {
        margin: 0.5in !important;
      }
      
      /* Avoid page breaks inside important sections */
      .ward-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      
      /* Header information */
      .print-header {
        text-align: center !important;
        margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 10px !important;
      }
      
      /* Make sure colors are visible */
      .shift-assigned {
        background-color: #4caf50 !important;
        color: white !important;
      }
      
      .shift-weekend-assigned {
        background-color: #ff9800 !important;
        color: white !important;
      }
      
      .shift-available {
        background-color: #2196f3 !important;
        color: white !important;
      }
      
      .shift-weekend-available {
        background-color: #ffb74d !important;
        color: black !important;
      }
      
      /* Ward colors */
      .ward-even {
        background-color: #f9f9f9 !important;
      }
      
      .ward-odd {
        background-color: white !important;
      }
      
      .ward-weekend {
        background-color: rgba(255, 152, 0, 0.05) !important;
      }
    }
  `}</style>
);

const WardWeeklyView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [selectedWardFilter, setSelectedWardFilter] = useState('all');
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [selectedDayFilter, setSelectedDayFilter] = useState('all');
  const [wards, setWards] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaffList, setFilteredStaffList] = useState([]);
  const [wardShifts, setWardShifts] = useState({});
  const [draggedStaff, setDraggedStaff] = useState(null);
  const [shiftToEdit, setShiftToEdit] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedWardForEmail, setSelectedWardForEmail] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMethod, setEmailMethod] = useState('outlook');
  const [includeWeekend, setIncludeWeekend] = useState(true);
  
  // Add state for shift release/unassign functionality
  const [shiftToRelease, setShiftToRelease] = useState(null);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  // Add state for creating new shifts
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [newShiftInfo, setNewShiftInfo] = useState({
    ward: '',
    dayDate: '',
    dayInfo: null,
  });

    // Add tabs state for edit dialog
    const [activeTab, setActiveTab] = useState('basic');
    
    // Enhanced edit form state
    const [editForm, setEditForm] = useState({
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      selectedPreset: '',
      hospitalLocationId: '',
      wardDepartmentArea: '',
      categoryOfStaff: '',
      reasonForRequest: '',
      reasonForRequestDetails: '',
      escort: false,
      special: false,
      isOvertime: false,
      isActive: true,
      eccNumber: '',
      specialRequirements: '',
      comments: '',
    });
    
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: '',
      severity: 'success'
    });
    
    const { userInfo } = useSelector((state) => state.auth);
    
    // Add to your existing mutation hooks (around line 270-280)
    const [createShift] = useCreateShiftMutation();

    // Refs to prevent infinite loops
    const hasSetFluid = useRef(false);
    
    // Get week dates (keep existing code)
    const getWeekDates = useCallback((date) => {
      const current = new Date(date);
      current.setHours(0, 0, 0, 0);
      
      const day = current.getDay();
      const diff = current.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(current.setDate(diff));
      
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      
      return {
        startDate: monday.toISOString().split('T')[0],
        endDate: sunday.toISOString().split('T')[0],
        fullEndDate: sunday.toISOString(),
        days: Array.from({ length: 7 }, (_, i) => {
          const day = new Date(monday);
          day.setDate(monday.getDate() + i);
          const dayOfWeek = day.getDay();
          return {
            date: day.toISOString().split('T')[0],
            shortName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
            dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek],
            dayNumber: dayOfWeek, // 0 = Sunday, 1 = Monday, etc.
            isWeekend: dayOfWeek === 0 || dayOfWeek === 6
          };
        })
      };
    }, []);
    
    const weekInfo = getWeekDates(selectedWeek);
    
    // Fetch data
    const { data: shiftsData, isLoading: loadingShifts, refetch: refetchShifts } = useGetWeeklyShiftsByWardQuery({
      startDate: weekInfo.startDate,
      endDate: weekInfo.endDate,
      endDate: weekInfo.fullEndDate, 
      ward: selectedWardFilter === 'all' ? '' : selectedWardFilter
    }, {
      skip: !weekInfo.startDate
    });
    
    const { data: staffData, isLoading: loadingStaff } = useGetUsersQuery();
    
    // Fetch availabilities for the week
    const { data: availabilitiesData, isLoading: loadingAvailabilities } = useGetWeeklyAvailabilitiesByStaffQuery({
      startDate: weekInfo.startDate,
      endDate: weekInfo.fullEndDate
    }, {
      skip: !weekInfo.startDate
    });
    
    // Fetch hospital locations
    const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery();
    
    const [assignStaff] = useAssignStaffToShiftMutation();
    const [updateShift] = useUpdateShiftMutation();
    const [reOpenShift] = useReOpenShiftMutation();  // Add this mutation

    useEffect(() => {
      if (!hasSetFluid.current) {
        setFluid(true);
        hasSetFluid.current = true;
      }
      
      return () => {
        if (hasSetFluid.current) {
          setFluid(false);
          hasSetFluid.current = false;
        }
      };
    }, [setFluid]);
    
    // Process availabilities data
    useEffect(() => {
      if (availabilitiesData) {
        console.log('Availabilities data loaded:', availabilitiesData);
      }
    }, [availabilitiesData]);
    
    // Group availabilities by staff member
    const getStaffAvailabilities = useCallback((staffId) => {
      if (!availabilitiesData || !Array.isArray(availabilitiesData)) return [];
      
      return availabilitiesData.filter(availability => {
        const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
        return availabilityStaffId === staffId;
      });
    }, [availabilitiesData]);
    
    // Check if staff has availabilities
    const staffHasAvailability = useCallback((staffId) => {
      return getStaffAvailabilities(staffId).length > 0;
    }, [getStaffAvailabilities]);
    
    // Format availability for display
    // Update formatAvailabilityDisplay to handle both sick leave and unavailability
    const formatAvailabilityDisplay = useCallback((availability) => {
      if (!availability) return '';
      
      const startDate = new Date(availability.startDateTime);
      const endDate = new Date(availability.endDateTime);
      
      const dayName = startDate.toLocaleDateString('en-US', { weekday: 'long' });
      const dateStr = startDate.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'short' 
      });
      
      const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      const ward = availability.wardDepartmentArea || '';
      
      let display = `${dayName} ${dateStr} ${startTime}-${endTime}`;
      if (ward) display += ` (${ward})`;
      
      // Update to show unavailability instead of sick leave
      if (availability.isUnavailable || availability.isSickLeave) {
        const reason = availability.unavailabilityReason || availability.sicknessReason || 'Unavailable';
        display += ` [${reason.toUpperCase()}]`;
      }
      
      return display;
    }, []);

    // Update useEffect to handle wardDepartmentArea based on selected hospital location
    useEffect(() => {
      if (editForm.hospitalLocationId && hospitalLocations.length > 0) {
        const selectedLocation = hospitalLocations.find(
          (loc) => loc._id === editForm.hospitalLocationId
        );
        if (selectedLocation) {
          setEditForm(prev => ({
            ...prev,
            wardDepartmentArea: selectedLocation.wardDepartmentArea || ''
          }));
        }
      }
    }, [editForm.hospitalLocationId, hospitalLocations]);

    useEffect(() => {
      if (shiftsData) {
        const uniqueWards = [...new Set(shiftsData.map(shift => shift.wardDepartmentArea))];
        setWards(uniqueWards);
        
        const shiftsByWardAndDay = {};
        
        uniqueWards.forEach(ward => {
          shiftsByWardAndDay[ward] = {};
          weekInfo.days.forEach(day => {
            shiftsByWardAndDay[ward][day.date] = [];
          });
        });
        
        shiftsData.forEach(shift => {
          const ward = shift.wardDepartmentArea;
          const shiftDate = new Date(shift.startDateTime).toISOString().split('T')[0];
          const dayOfWeek = new Date(shift.startDateTime).getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          if (shiftsByWardAndDay[ward] && shiftsByWardAndDay[ward][shiftDate]) {
            shiftsByWardAndDay[ward][shiftDate].push({
              id: shift._id,
              startTime: new Date(shift.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              endTime: new Date(shift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              ward: ward,
              location: shift.hospitalLocationId?.name,
              staffName: shift.filledBy?.name || shift.filledByAgency?.name || 'Available',
              staffId: shift.filledBy?._id || shift.filledByAgency?._id,
              staffEmail: shift.filledBy?.email || shift.filledByAgency?.email,
              isFilled: !!shift.filledBy || !!shift.filledByAgency,
              isWeekend: isWeekend,
              shiftData: shift
            });
          }
        });
        
        setWardShifts(shiftsByWardAndDay);
      }
    }, [shiftsData, weekInfo.days]);
    
    // NEW: Check if staff has availability for a specific day
    const staffHasAvailabilityForDay = useCallback((staffId, dayName) => {
      if (!availabilitiesData || !Array.isArray(availabilitiesData) || dayName === 'all') {
        return true; // Return true if no filter or all days selected
      }
      
      const staffAvailabilities = getStaffAvailabilities(staffId);
      if (staffAvailabilities.length === 0) {
        return false; // No availability data for this staff
      }
      
      // Find the day object from weekInfo
      const dayObj = weekInfo.days.find(day => day.dayName === dayName);
      if (!dayObj) return false;
      
      const filterDate = dayObj.date;
      
      // Check if staff has any availability (not unavailable) on this specific date
      const hasAvailabilityOnDate = staffAvailabilities.some(availability => {
        const availabilityDate = new Date(availability.startDateTime).toISOString().split('T')[0];
        const isSameDate = availabilityDate === filterDate;
        const isAvailable = !(availability.isUnavailable || availability.isSickLeave);
        
        return isSameDate && isAvailable;
      });
      
      return hasAvailabilityOnDate;
    }, [availabilitiesData, weekInfo.days, getStaffAvailabilities]);
    
    // Sort and filter staff with the new day filter
    const sortedAndFilteredStaff = useMemo(() => {
      if (!staffData) return [];
      
      let filtered = staffData.filter(user => !user.isAdmin && !user.isAgency);
      
      // Apply job title filter
      if (selectedJobTitleFilter !== 'all') {
        filtered = filtered.filter(staff => 
          staff.jobtitle === selectedJobTitleFilter
        );
      }
      
      // Apply day availability filter
      if (selectedDayFilter !== 'all') {
        filtered = filtered.filter(staff => 
          staffHasAvailabilityForDay(staff._id, selectedDayFilter)
        );
      }
      
      return filtered.sort((a, b) => {
        const aHasAvailability = staffHasAvailability(a._id);
        const bHasAvailability = staffHasAvailability(b._id);
        
        if (aHasAvailability && !bHasAvailability) return -1;
        if (!aHasAvailability && bHasAvailability) return 1;
        
        return a.name.localeCompare(b.name);
      });
    }, [staffData, selectedJobTitleFilter, selectedDayFilter, staffHasAvailability, staffHasAvailabilityForDay]);
    
    useEffect(() => {
      setFilteredStaffList(sortedAndFilteredStaff);
    }, [sortedAndFilteredStaff]);
    
    // Count staff with and without availabilities
    const staffWithAvailabilitiesCount = useMemo(() => {
      return filteredStaffList.filter(staff => staffHasAvailability(staff._id)).length;
    }, [filteredStaffList, staffHasAvailability]);
    
    // Extract unique job titles for filter dropdown
    const jobTitles = React.useMemo(() => {
      if (!staffData) return ['all'];
      const titles = [...new Set(staffData
        .filter(user => !user.isAdmin && !user.isAgency)
        .map(user => user.jobtitle)
        .filter(title => title && title.trim() !== '')
      )].sort();
      return ['all', ...titles];
    }, [staffData]);

    // Create day options for filter (Monday to Sunday)
    const dayOptions = React.useMemo(() => {
      const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return ['all', ...allDays];
    }, []);

    // First, define the checkStaffHasUnavailability function BEFORE handleDrop
    const checkStaffHasUnavailability = useCallback((staffId, shiftDate) => {
      if (!availabilitiesData || !Array.isArray(availabilitiesData)) {
        console.log('❌ No availabilities data');
        return false;
      }
      
      const staffAvailabilities = availabilitiesData.filter(availability => {
        const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
        return availabilityStaffId === staffId;
      });
      
      console.log('🔍 Checking unavailability for:', {
        staffId,
        shiftDate,
        totalAvailabilities: staffAvailabilities.length
      });

      // Check if any availability is marked as unavailable on the SPECIFIC shift date
      const hasUnavailabilityOnThisDate = staffAvailabilities.some(availability => {
        const availabilityDate = new Date(availability.startDateTime).toISOString().split('T')[0];
        const isSameDate = availabilityDate === shiftDate;
        
        // Check for both field names
        const isUnavailable = availability.isUnavailable === true || availability.isSickLeave === true;
        
        const result = isSameDate && isUnavailable;
        
        if (result) {
          console.log('❌ Found unavailability on this date:', {
            availabilityDate,
            shiftDate,
            reason: availability.unavailabilityReason || availability.sicknessReason,
            isSameDate,
            isUnavailable
          });
        }
        
        return result;
      });
      
      console.log('📊 Unavailability check result for date', shiftDate, ':', hasUnavailabilityOnThisDate);
      return hasUnavailabilityOnThisDate;
    }, [availabilitiesData]);

    // Update the function to get unavailability info
    const getStaffUnavailabilityInfo = useCallback((staffId) => {
      if (!availabilitiesData || !Array.isArray(availabilitiesData)) return [];
      
      const staffAvailabilities = availabilitiesData.filter(availability => {
        const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
        return availabilityStaffId === staffId;
      });
      
      return staffAvailabilities
        .filter(avail => avail.isUnavailable || avail.isSickLeave)
        .map(avail => {
          const date = new Date(avail.startDateTime).toLocaleDateString();
          const reason = avail.unavailabilityReason || avail.sicknessReason || 'No reason specified';
          const type = avail.unavailabilityType || avail.sickLeaveType || 'Full Day';
          return { date, reason, type };
        });
    }, [availabilitiesData]);

    // Add this function near your other helper functions (around line 240-250)
    const checkStaffHasSickLeave = useCallback((staffId, shiftDate) => {
      if (!availabilitiesData || !Array.isArray(availabilitiesData)) return false;
      
      const staffAvailabilities = availabilitiesData.filter(availability => {
        const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
        return availabilityStaffId === staffId;
      });
      
      // Check if any availability is sick leave on the shift date
      return staffAvailabilities.some(availability => {
        const availabilityDate = new Date(availability.startDateTime).toISOString().split('T')[0];
        const isSameDate = availabilityDate === shiftDate;
        return isSameDate && availability.isSickLeave === true;
      });
    }, [availabilitiesData]);

    // Filter wards based on selection
    const filteredWards = selectedWardFilter === 'all' 
      ? wards 
      : wards.filter(ward => ward === selectedWardFilter);

    // Handle preset selection
    const handlePresetSelect = (presetKey) => {
      const preset = SHIFT_PRESETS[presetKey];
      if (preset) {
        setEditForm(prev => ({
          ...prev,
          selectedPreset: presetKey,
          startTime: preset.start,
          endTime: preset.end,
          escort: presetKey === 'ESCORT'
        }));
      }
    };

    // Check if staff has availability that fits within shift timeline

    // Calculate shift duration
    const calculateShiftDuration = () => {
      if (!editForm.startDate || !editForm.startTime || !editForm.endDate || !editForm.endTime) {
        return 'Please enter dates and times';
      }
      
      const startDateTime = new Date(`${editForm.startDate}T${editForm.startTime}`);
      let endDateTime = new Date(`${editForm.endDate}T${editForm.endTime}`);
      
      // Handle overnight shifts
      if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }
      
      if (endDateTime <= startDateTime) {
        return 'Invalid: End time must be after start time';
      }
      
      const durationMs = endDateTime - startDateTime;
      const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
      const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
      
      return `${durationHours} hours ${durationMinutes} minutes`;
    };

    // Get filter summary
    const getFilterSummary = () => {
      const filters = [];
      if (selectedWardFilter !== 'all') filters.push(`Ward: ${selectedWardFilter}`);
      if (selectedJobTitleFilter !== 'all') filters.push(`Job: ${selectedJobTitleFilter}`);
      if (selectedDayFilter !== 'all') filters.push(`Day: ${selectedDayFilter}`);
      return filters.length > 0 ? filters.join(' • ') : 'No filters applied';
    };

    // Clear all filters
    const clearAllFilters = () => {
      setSelectedWardFilter('all');
      setSelectedJobTitleFilter('all');
      setSelectedDayFilter('all');
    };

    // Handle drag and drop
    const handleDragStart = (e, staff) => {
      console.log('🚀 DRAG START:', staff.name, staff._id);

      setDraggedStaff(staff);
      e.dataTransfer.setData('text/plain', JSON.stringify(staff));
      
      // Add a visual warning if staff has unavailability
      const unavailabilityInfo = getStaffUnavailabilityInfo(staff._id);
      if (unavailabilityInfo.length > 0) {
        console.log('⚠️ Staff has unavailability:', unavailabilityInfo);
        e.dataTransfer.setData('unavailabilityWarning', 'Staff has unavailability marked');
      }
    };

    // Define checkStaffAvailabilityForShift BEFORE handleDrop as well
    const checkStaffAvailabilityForShift = useCallback((staffId, shift) => {
      if (!staffId || !shift || !shift.shiftData) {
        console.log('Missing required data');
        return false;
      }
      
      // First check if staff has unavailability on this date
      const shiftDate = new Date(shift.shiftData.startDateTime).toISOString().split('T')[0];

      console.log('📅 Checking availability for:', {
        staffId,
        shiftDate,
        shiftId: shift.id,
        shiftTime: `${shift.startTime} - ${shift.endTime}`,
        shiftCategory: shift.shiftData?.categoryOfStaff
      });

      const hasUnavailability = checkStaffHasUnavailability(staffId, shiftDate);
      
      if (hasUnavailability) {
        console.log('Staff has unavailability on this date');
        return false;
      }
      
      const staffAvailabilities = getStaffAvailabilities(staffId);
      console.log('📋 Staff availabilities:', staffAvailabilities.length, 'total');

      if (staffAvailabilities.length === 0) {
        console.log('Staff has no availabilities');
        return false;
      }
      
      const shiftStart = new Date(shift.shiftData.startDateTime);
      const shiftEnd = new Date(shift.shiftData.endDateTime);
      const shiftCategory = shift.shiftData?.categoryOfStaff;
      
      // Find staff from staffData to get their jobtitle
      const staff = staffData?.find(s => s._id === staffId);
      const staffJobTitle = staff?.jobtitle;
      
      console.log('👤 Staff info:', {
        name: staff?.name,
        jobtitle: staffJobTitle,
        shiftCategory,
        categoryMatch: shiftCategory === staffJobTitle
      });

      console.log('Category Check:', {
        shiftCategory,
        staffJobTitle,
        match: shiftCategory === staffJobTitle
      });
      
      // Check each availability for this staff
      for (const availability of staffAvailabilities) {
        // Skip unavailable periods
        if (availability.isUnavailable || availability.isSickLeave) {
          console.log('⚠️ Skipping unavailable availability:', availability);
          continue;
        }

        
        const availabilityStart = new Date(availability.startDateTime);
        const availabilityEnd = new Date(availability.endDateTime);
        
        // Check if availability is on the same DATE
        const availabilityDate = availabilityStart.toISOString().split('T')[0];

        console.log('🔍 Checking availability:', {
          availabilityDate,
          shiftDate,
          isSameDate: availabilityDate === shiftDate,
          availabilityTimes: `${availabilityStart.toLocaleTimeString()} - ${availabilityEnd.toLocaleTimeString()}`,
          shiftTimes: `${shiftStart.toLocaleTimeString()} - ${shiftEnd.toLocaleTimeString()}`
        });

        
        if (availabilityDate === shiftDate) {
          // Check if shift time fits within availability time
          const shiftStartTime = shiftStart.getTime();
          const shiftEndTime = shiftEnd.getTime();
          const availabilityStartTime = availabilityStart.getTime();
          const availabilityEndTime = availabilityEnd.getTime();
          
          const timeMatches = shiftStartTime >= availabilityStartTime && 
                            shiftEndTime <= availabilityEndTime;
          
          console.log('Time Match Check:', {
            availabilityDate,
            shiftDate,
            shiftTimes: `${shiftStart.toLocaleTimeString()} - ${shiftEnd.toLocaleTimeString()}`,
            availabilityTimes: `${availabilityStart.toLocaleTimeString()} - ${availabilityEnd.toLocaleTimeString()}`,
            shiftStartTime,
            shiftEndTime,
            availabilityStartTime,
            availabilityEndTime,
            timeMatches
          });
          
          if (timeMatches) {
            // If category is specified on shift, check if it matches staff's jobtitle
            if (shiftCategory && staffJobTitle) {
              // Try partial matching (e.g., "Bank-HCA" contains "HCA")
              const categoryMatch = staffJobTitle.includes(shiftCategory) || 
                                  shiftCategory.includes(staffJobTitle) ||
                                  staffJobTitle === shiftCategory;
              
              console.log('Category Match Result:', {
                shiftCategory,
                staffJobTitle,
                partialMatch: staffJobTitle.includes(shiftCategory),
                categoryMatch
              });
              
              if (!categoryMatch) {
                console.log('❌ Category mismatch prevents assignment');
                return false;
              }
            }
            
            console.log('✅ All checks passed - staff can be assigned!');
            return true;
          } else {
            console.log('❌ Times do not match');
          }
        } else {
          console.log('❌ Dates do not match');
        }
      }
      
      console.log('No matching availability found');
      return false;
    }, [getStaffAvailabilities, checkStaffHasUnavailability, staffData]);

    const handleDragOver = (e) => {
      e.preventDefault();
    };
    
    // Now define handleDrop (AFTER the functions it depends on)
    const handleDrop = async (e, shift) => {
      e.preventDefault();
      
      if (!draggedStaff || !shift) return;
      
      console.log('=== DRAG & DROP DEBUG START ===');
      console.log('Dragged Staff:', {
        name: draggedStaff.name,
        id: draggedStaff._id,
        jobtitle: draggedStaff.jobtitle,
        email: draggedStaff.email
      });
      
      console.log('Target Shift:', {
        id: shift.id,
        date: new Date(shift.shiftData.startDateTime).toISOString().split('T')[0],
        time: `${shift.startTime} - ${shift.endTime}`,
        category: shift.shiftData?.categoryOfStaff,
        ward: shift.ward,
        isFilled: shift.isFilled
      });
      
      // Check if staff has unavailability on this date
      const shiftDate = new Date(shift.shiftData.startDateTime).toISOString().split('T')[0];
      const hasUnavailability = checkStaffHasUnavailability(draggedStaff._id, shiftDate);
      
      console.log('Unavailability Check Result:', {
        hasUnavailability,
        shiftDate
      });
      
      if (hasUnavailability) {
        const unavailabilityInfo = getStaffUnavailabilityInfo(draggedStaff._id);
        const todayUnavailability = unavailabilityInfo.find(unavail => {
          const unavailDate = new Date(unavail.date).toISOString().split('T')[0];
          return unavailDate === shiftDate;
        });

        if (todayUnavailability) {
          showSnackbar(
            `${draggedStaff.name} is unavailable on ${shiftDate}. Reason: ${todayUnavailability.reason}`,
            'error'
          );
        } else {
          // This shouldn't happen, but just in case
          showSnackbar(
            `${draggedStaff.name} is marked as unavailable on this date. Cannot assign to shift.`,
            'error'
          );
        }
        return;
      }
      
      // Check if staff has availability for this shift
      const hasAvailability = checkStaffAvailabilityForShift(draggedStaff._id, shift);
      
      console.log('Availability Check Result:', hasAvailability);
      console.log('=== DRAG & DROP DEBUG END ===');
      
      if (!hasAvailability) {
        // Check why availability check failed
        const staffAvailabilities = getStaffAvailabilities(draggedStaff._id);
        console.log('Staff availabilities:', staffAvailabilities);
        
        // Check category mismatch
        const shiftCategory = shift.shiftData?.categoryOfStaff;
        const staffJobTitle = draggedStaff.jobtitle;
        
        // Get more specific error message
        let errorMessage = `${draggedStaff.name} does not have availability for this shift`;

        if (shiftCategory && staffJobTitle && shiftCategory !== staffJobTitle) {
          errorMessage = `${draggedStaff.name} (${staffJobTitle}) does not match required category: ${shiftCategory}`;
        } else {
          // Check if it's a date mismatch
          const shiftDate = new Date(shift.shiftData.startDateTime).toISOString().split('T')[0];
          const hasAvailabilityOnDate = staffAvailabilities.some(avail => {
            const availDate = new Date(avail.startDateTime).toISOString().split('T')[0];
            return availDate === shiftDate && !(avail.isUnavailable || avail.isSickLeave);
          });
          
          if (!hasAvailabilityOnDate) {
            errorMessage = `${draggedStaff.name} has no availability on ${shiftDate}`;
          } else {
            errorMessage = `${draggedStaff.name} has availability but times don't match`;
          }
        }
        
        showSnackbar(errorMessage, 'error');
        return;
      }
      
      try {
        console.log('Attempting to assign staff to shift...');
        
        await assignStaff({
          shiftId: shift.id,
          staffData: {
            userId: draggedStaff._id,
            userName: draggedStaff.name,
            userEmail: draggedStaff.email,
            managerId: userInfo._id
          }
        }).unwrap();
        
        console.log('Assignment successful, updating local state...');
        
        const date = new Date(shift.shiftData.startDateTime).toISOString().split('T')[0];
        const updatedShifts = { ...wardShifts };
        
        if (updatedShifts[shift.ward] && updatedShifts[shift.ward][date]) {
          const shiftIndex = updatedShifts[shift.ward][date].findIndex(s => s.id === shift.id);
          if (shiftIndex !== -1) {
            updatedShifts[shift.ward][date][shiftIndex] = {
              ...updatedShifts[shift.ward][date][shiftIndex],
              staffName: draggedStaff.name,
              staffId: draggedStaff._id,
              staffEmail: draggedStaff.email,
              isFilled: true
            };
            setWardShifts(updatedShifts);
          }
        }
        
        refetchShifts();
        
        setDraggedStaff(null);
        showSnackbar(`${draggedStaff.name} assigned to shift successfully!`, 'success');
        
      } catch (error) {
        console.error('Failed to assign staff to shift:', error);
        showSnackbar('Failed to assign staff to shift', 'error');
      }
    };
    
    // Enhanced edit shift click handler
    const handleEditShiftClick = (shift) => {
      if (!shift || !shift.shiftData) return;
      
      const shiftData = shift.shiftData;
      const startDateTime = new Date(shiftData.startDateTime);
      const endDateTime = new Date(shiftData.endDateTime);
      
      // Determine which preset matches the shift times
      const startTime = startDateTime.toTimeString().slice(0, 5);
      const endTime = endDateTime.toTimeString().slice(0, 5);
      
      let selectedPreset = '';
      Object.entries(SHIFT_PRESETS).forEach(([key, preset]) => {
        if (preset.start === startTime && preset.end === endTime) {
          selectedPreset = key;
        }
      });
      
      setShiftToEdit({
        shiftId: shift.id,
        ward: shift.ward,
        dayDate: new Date(shiftData.startDateTime).toISOString().split('T')[0],
        staffName: shift.staffName,
        staffId: shift.staffId
      });
      
      // Pre-fill the edit form with all fields
      setEditForm({
        startDate: startDateTime.toISOString().split('T')[0],
        startTime: startTime,
        endDate: endDateTime.toISOString().split('T')[0],
        endTime: endTime,
        selectedPreset: selectedPreset,
        hospitalLocationId: shiftData.hospitalLocationId?._id || shiftData.hospitalLocationId || '',
        wardDepartmentArea: shiftData.wardDepartmentArea || '',
        categoryOfStaff: shiftData.categoryOfStaff || '',
        reasonForRequest: shiftData.reasonForRequest || '',
        reasonForRequestDetails: shiftData.reasonForRequestDetails || '',
        escort: shiftData.escort || false,
        special: shiftData.special || false,
        isOvertime: shiftData.isOvertime || false,
        isActive: shiftData.isActive !== false,
        eccNumber: shiftData.eccNumber || '',
        specialRequirements: shiftData.specialRequirements || '',
        comments: shiftData.comments || '',
      });
      
      setActiveTab('basic');
      setEditDialogOpen(true);
    };
    
    // Function to handle shift double-click for unassigning
    const handleShiftDoubleClick = (shift) => {
      if (!shift || !shift.isFilled) return;
      
      setShiftToRelease({
        shiftId: shift.id,
        staffId: shift.staffId,
        staffName: shift.staffName,
        ward: shift.ward,
        dayDate: new Date(shift.shiftData.startDateTime).toISOString().split('T')[0],
        shiftTime: `${shift.startTime} - ${shift.endTime}`,
      });
      
      setReleaseDialogOpen(true);
    };
    
    // Function to handle releasing/unassigning a shift
    const handleReleaseShift = async () => {
      if (!shiftToRelease) return;
      
      try {
        // Call reOpenShift mutation to unassign the staff
        await reOpenShift({
          shiftId: shiftToRelease.shiftId,
          userId: shiftToRelease.staffId,
          userName: shiftToRelease.staffName,
          userEmail: '', // You might want to get this from staff data
          managerId: userInfo._id
        }).unwrap();
        
        // Update local state - remove staff from shift
        const updatedShifts = { ...wardShifts };
        if (updatedShifts[shiftToRelease.ward] && updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate]) {
          const shiftIndex = updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate].findIndex(
            s => s.id === shiftToRelease.shiftId
          );
          
          if (shiftIndex !== -1) {
            updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate][shiftIndex] = {
              ...updatedShifts[shiftToRelease.ward][shiftToRelease.dayDate][shiftIndex],
              staffName: 'Available',
              staffId: null,
              staffEmail: null,
              isFilled: false
            };
            setWardShifts(updatedShifts);
          }
        }
        
        // Refetch data to ensure consistency
        refetchShifts();
        
        showSnackbar('Shift released successfully! Shift is now available for assignment.', 'success');
        
      } catch (error) {
        console.error('Failed to release shift:', error);
        showSnackbar('Failed to release shift', 'error');
      } finally {
        setReleaseDialogOpen(false);
        setShiftToRelease(null);
      }
    };
    
    const handleCancelRelease = () => {
      setReleaseDialogOpen(false);
      setShiftToRelease(null);
    };

    // Function to handle creating new shift
    const handleCreateShiftClick = (ward, day) => {
      // Get hospital location for this ward
      const hospitalLocation = hospitalLocations.find(
        loc => loc.wardDepartmentArea === ward
      );
      
      setNewShiftInfo({
        ward: ward,
        dayDate: day.date,
        dayInfo: day,
      });
      
      // Pre-fill the edit form with default values based on ward
      const startTime = '08:00';
      const endTime = '20:00';
      
      // Calculate dates
      const startDate = day.date;
      const endDate = day.date; // Same day by default
      
      setEditForm({
        startDate: startDate,
        startTime: startTime,
        endDate: endDate,
        endTime: endTime,
        selectedPreset: 'LD', // Long Day by default
        hospitalLocationId: hospitalLocation?._id || '',
        wardDepartmentArea: ward || '',
        categoryOfStaff: '',
        reasonForRequest: '',
        reasonForRequestDetails: '',
        escort: false,
        special: false,
        isOvertime: false,
        isActive: true,
        eccNumber: '',
        specialRequirements: '',
        comments: `New shift for ${ward} on ${day.dayName}`,
      });
      
      setActiveTab('schedule');
      setCreateDialogOpen(true);
    };

    // Function to create new shift
    const handleCreateShift = async () => {
      if (!newShiftInfo.ward || !newShiftInfo.dayDate) return;
      
      try {
        // Combine date and time into ISO strings
        const combineDateTime = (dateStr, timeStr) => {
          const date = new Date(`${dateStr}T${timeStr}`);
          
          // Handle overnight shifts
          if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
            date.setDate(date.getDate() + 1);
          }
          
          return date.toISOString();
        };
        
        // Prepare shift data
        const shiftData = {
          startDateTime: combineDateTime(editForm.startDate, editForm.startTime),
          endDateTime: combineDateTime(editForm.endDate, editForm.endTime),
          hospitalLocationId: editForm.hospitalLocationId,
          wardDepartmentArea: editForm.wardDepartmentArea,
          reasonForRequest: editForm.reasonForRequest,
          reasonForRequestDetails: editForm.reasonForRequestDetails,
          categoryOfStaff: editForm.categoryOfStaff,
          escort: editForm.escort,
          special: editForm.special,
          isOvertime: editForm.isOvertime,
          isActive: editForm.isActive,
          eccNumber: editForm.eccNumber,
          specialRequirements: editForm.specialRequirements,
          comments: editForm.comments,
        };
        
        // Call create shift API
        const result = await createShift({
          numberOfShifts: 1,
          shiftDates: [], // Single shift
          ...shiftData
        }).unwrap();
        
        // Refetch shifts to update the view
        refetchShifts();
        
        showSnackbar(`New shift created successfully in ${newShiftInfo.ward}!`, 'success');
        setCreateDialogOpen(false);
        setNewShiftInfo({ ward: '', dayDate: '', dayInfo: null });
        
      } catch (error) {
        console.error('Failed to create shift:', error);
        showSnackbar('Failed to create shift', 'error');
      }
    };
    
    // Enhanced update shift handler
    const handleUpdateShift = async () => {
      if (!shiftToEdit || !shiftToEdit.shiftId) return;
      
      try {
        // Combine date and time into ISO strings
        const combineDateTime = (dateStr, timeStr) => {
          const date = new Date(`${dateStr}T${timeStr}`);
          
          // Handle overnight shifts
          if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
            date.setDate(date.getDate() + 1);
          }
          
          return date.toISOString();
        };
        
        // Format dates for backend
        const formattedData = {
          shiftId: shiftToEdit.shiftId,
          startDateTime: combineDateTime(editForm.startDate, editForm.startTime),
          endDateTime: combineDateTime(editForm.endDate, editForm.endTime),
          hospitalLocationId: editForm.hospitalLocationId,
          wardDepartmentArea: editForm.wardDepartmentArea,
          reasonForRequest: editForm.reasonForRequest,
          reasonForRequestDetails: editForm.reasonForRequestDetails,
          categoryOfStaff: editForm.categoryOfStaff,
          escort: editForm.escort,
          special: editForm.special,
          isOvertime: editForm.isOvertime,
          isActive: editForm.isActive,
          eccNumber: editForm.eccNumber,
          specialRequirements: editForm.specialRequirements,
          comments: editForm.comments,
        };
        
        await updateShift(formattedData).unwrap();
        
        // Update local state
        const updatedShifts = { ...wardShifts };
        if (updatedShifts[shiftToEdit.ward] && updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate]) {
          const shiftIndex = updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate].findIndex(
            s => s.id === shiftToEdit.shiftId
          );
          
          if (shiftIndex !== -1) {
            const updatedShift = {
              ...updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate][shiftIndex],
              startTime: new Date(formattedData.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              endTime: new Date(formattedData.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              ward: editForm.wardDepartmentArea,
              categoryOfStaff: editForm.categoryOfStaff,
            };
            
            updatedShifts[shiftToEdit.ward][shiftToEdit.dayDate][shiftIndex] = updatedShift;
            setWardShifts(updatedShifts);
          }
        }
        
        // Refetch data
        refetchShifts();
        
        showSnackbar('Shift updated successfully!', 'success');
        setEditDialogOpen(false);
        setShiftToEdit(null);
        
      } catch (error) {
        console.error('Failed to update shift:', error);
        showSnackbar('Failed to update shift', 'error');
      }
    };
    
    const handleCancelEdit = () => {
      setEditDialogOpen(false);
      setShiftToEdit(null);
    };
    
    const handleEditFormChange = (e) => {
      const { name, value, type, checked } = e.target;
      setEditForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };

    // Add this function near your other helper functions
    const getStaffSickLeaveInfo = useCallback((staffId) => {
      if (!availabilitiesData || !Array.isArray(availabilitiesData)) return [];
      
      const staffAvailabilities = availabilitiesData.filter(availability => {
        const availabilityStaffId = availability.requestedBy?._id || availability.requestedBy;
        return availabilityStaffId === staffId;
      });
      
      return staffAvailabilities
        .filter(avail => avail.isSickLeave)
        .map(avail => {
          const date = new Date(avail.startDateTime).toLocaleDateString();
          const reason = avail.sicknessReason || 'No reason specified';
          return { date, reason };
        });
    }, [availabilitiesData]);

    // PRINT FUNCTION
    const handlePrint = () => {
      // Create a printable version of the ward calendar
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        showSnackbar('Please allow pop-ups to print', 'warning');
        return;
      }
      
      // Get the current filter info
      const filterInfo = getFilterSummary();
      
      // Build the printable HTML
      const printHTML = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Ward Weekly Schedule - ${formatDateRange()}</title>
          <style>
            @page {
              size: landscape;
              margin: 0.5in;
            }
            
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            
            .print-title {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .print-subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 10px;
            }
            
            .print-info {
              font-size: 12px;
              color: #666;
              margin-bottom: 5px;
            }
            
            .print-calendar {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              margin-top: 20px;
            }
            
            .print-calendar th {
              background-color: #1976d2;
              color: white;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            
            .print-calendar td {
              padding: 5px;
              border: 1px solid #ddd;
              vertical-align: top;
              min-height: 120px;
            }
            
            .ward-header {
              background-color: #1976d2;
              color: white;
              padding: 8px;
              text-align: center;
              font-weight: bold;
              width: 200px;
            }
            
            .day-header {
              background-color: #1976d2;
              color: white;
              padding: 8px;
              text-align: center;
              font-weight: bold;
            }
            
            .weekend-header {
              background-color: #ff9800 !important;
            }
            
            .ward-cell {
              padding: 5px;
              border-bottom: 1px solid #eee;
            }
            
            .shift-card {
              padding: 4px;
              margin: 2px 0;
              border-radius: 4px;
              font-size: 10px;
              color: white;
              font-weight: bold;
              page-break-inside: avoid;
              break-inside: avoid;
              min-height: 50px;
            }
            
            .shift-assigned {
              background-color: #4caf50 !important;
              border: 1px solid #388e3c;
            }
            
            .shift-weekend-assigned {
              background-color: #ff9800 !important;
              border: 1px solid #f57c00;
            }
            
            .shift-available {
              background-color: #2196f3 !important;
              border: 1px solid #1565c0;
            }
            
            .shift-weekend-available {
              background-color: #ffb74d !important;
              border: 1px solid #ff9800;
              color: black !important;
            }
            
            .shift-time {
              font-weight: bold;
              font-size: 9px;
            }
            
            .shift-details {
              font-size: 8px;
              margin-top: 2px;
            }
            
            .staff-name {
              font-size: 9px;
              font-weight: bold;
            }
            
            .ward-info {
              padding: 8px;
              background-color: #f5f5f5;
              border-right: 1px solid #ddd;
              min-width: 200px;
            }
            
            .ward-name {
              font-weight: bold;
              font-size: 12px;
            }
            
            .ward-stats {
              font-size: 10px;
              color: #666;
              margin-top: 2px;
            }
            
            .legend {
              margin-top: 30px;
              padding: 15px;
              border: 1px solid #ddd;
              background-color: #f9f9f9;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            .legend-title {
              font-weight: bold;
              margin-bottom: 10px;
              font-size: 14px;
            }
            
            .legend-item {
              display: inline-block;
              margin-right: 20px;
              margin-bottom: 10px;
            }
            
            .legend-color {
              display: inline-block;
              width: 20px;
              height: 20px;
              margin-right: 5px;
              vertical-align: middle;
              border-radius: 3px;
              border: 1px solid #ddd;
            }
            
            .footer {
              margin-top: 20px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              font-size: 10px;
              color: #666;
              text-align: center;
            }
            
            @media print {
              .no-print {
                display: none !important;
              }
              
              body {
                margin: 0 !important;
                padding: 0 !important;
              }
              
              .print-calendar {
                width: 100% !important;
                font-size: 10px !important;
              }
              
              .shift-card {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              
              .ward-row {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Ward Weekly Schedule</div>
            <div class="print-subtitle">Week of ${formatDateRange()}</div>
            <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
            <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
            ${filterInfo !== 'No filters applied' ? `<div class="print-info">Filters: ${filterInfo}</div>` : ''}
            <div class="print-info">Showing ${filteredWards.length} wards</div>
          </div>
          
          <table class="print-calendar">
            <thead>
              <tr>
                <th class="ward-header">Ward Information</th>
                ${weekInfo.days.map(day => `
                  <th class="day-header ${day.isWeekend ? 'weekend-header' : ''}" style="width: calc((100% - 200px) / 7);">
                    ${day.dayName}<br>
                    <span style="font-size: 11px; font-weight: normal;">${day.date}</span>
                    ${day.isWeekend ? '<div style="font-size: 10px; font-style: italic;">Weekend</div>' : ''}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredWards.map((ward, index) => {
                const wardShiftsData = wardShifts[ward] || {};
                
                // Calculate ward statistics
                const allShifts = Object.values(wardShiftsData).flat();
                const totalShifts = allShifts.length;
                const filledShifts = allShifts.filter(s => s.isFilled).length;
                const availableShifts = allShifts.filter(s => !s.isFilled).length;
                const weekendShifts = allShifts.filter(s => s.isWeekend).length;
                
                return `
                  <tr class="ward-row">
                    <td class="ward-info" style="background-color: ${index % 2 === 0 ? '#f9f9f9' : 'white'};">
                      <div class="ward-name">${ward}</div>
                      <div class="ward-stats">
                        Total: ${totalShifts} shift${totalShifts !== 1 ? 's' : ''}<br>
                        Filled: ${filledShifts}<br>
                        Available: ${availableShifts}<br>
                        Weekend: ${weekendShifts}
                      </div>
                    </td>
                    ${weekInfo.days.map(day => {
                      const shifts = wardShiftsData[day.date] || [];
                      
                      return `
                        <td style="background-color: ${day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : (index % 2 === 0 ? '#f9f9f9' : 'white')};">
                          ${shifts.map(shift => {
                            let shiftClass = 'shift-card';
                            if (shift.isFilled) {
                              shiftClass += shift.isWeekend ? ' shift-weekend-assigned' : ' shift-assigned';
                            } else {
                              shiftClass += shift.isWeekend ? ' shift-weekend-available' : ' shift-available';
                            }
                            
                            return `
                              <div class="${shiftClass}">
                                <div class="shift-time">${shift.startTime} - ${shift.endTime}</div>
                                <div class="staff-name">${shift.staffName}</div>
                                <div class="shift-details">
                                  Status: ${shift.isFilled ? 'Assigned' : 'Available'}
                                  ${shift.isWeekend ? '• Weekend' : ''}
                                </div>
                                ${shift.location ? `<div class="shift-details">${shift.location}</div>` : ''}
                              </div>
                            `;
                          }).join('')}
                          
                          ${shifts.length === 0 ? `
                            <div style="text-align: center; color: #999; font-style: italic; font-size: 10px; padding: 20px 5px;">
                              No shifts
                            </div>
                          ` : ''}
                        </td>
                      `;
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="legend">
            <div class="legend-title">Color Legend:</div>
            <div class="legend-item">
              <span class="legend-color" style="background-color: #4caf50;"></span>
              <span>Assigned Shift (Weekday)</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background-color: #ff9800;"></span>
              <span>Assigned Shift (Weekend)</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background-color: #2196f3;"></span>
              <span>Available Shift (Weekday)</span>
            </div>
            <div class="legend-item">
              <span class="legend-color" style="background-color: #ffb74d;"></span>
              <span>Available Shift (Weekend)</span>
            </div>
          </div>
          
          <div class="footer">
            Beaumont Hospital Ward Scheduling System<br>
            Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
            For questions, contact: scheduling@beaumont.ie
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 100);
            };
          </script>
        </body>
        </html>
      `;
      
      printWindow.document.write(printHTML);
      printWindow.document.close();
    };

    // Get days to include in email (based on includeWeekend setting)
    const getEmailDays = () => {
      if (includeWeekend) {
        return weekInfo.days;
      } else {
        return weekInfo.days.filter(day => !day.isWeekend);
      }
    };

    // Generate HTML table for ward email
    const generateWardScheduleHTMLTable = (ward) => {
      const emailDays = getEmailDays();
      
      let html = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
          <h2 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px;">
            Weekly Ward Schedule - ${ward}
          </h2>
          
          <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 5px;"><strong>Week:</strong> ${formatDateRange()}</td>
                <td style="padding: 5px;"><strong>Ward:</strong> ${ward}</td>
              </tr>
              <tr>
                <td style="padding: 5px;"><strong>Generated:</strong> ${new Date().toLocaleDateString()}</td>
                <td style="padding: 5px;"><strong>Generated By:</strong> ${userInfo?.name || 'Manager'}</td>
              </tr>
              <tr>
                <td style="padding: 5px;"><strong>Includes Weekend:</strong> ${includeWeekend ? 'Yes' : 'No'}</td>
                <td style="padding: 5px;"><strong>Days Shown:</strong> ${emailDays.length} of ${weekInfo.days.length}</td>
              </tr>
            </table>
          </div>
          
          <div style="overflow-x: auto;">
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
              <thead>
                <tr style="background-color: #3498db; color: white;">
                  <th style="padding: 12px; text-align: left; width: 15%;">Day</th>
                  <th style="padding: 12px; text-align: left; width: 85%;">Shifts & Staff</th>
                </tr>
              </thead>
              <tbody>
      `;
      
      let hasShifts = false;
      
      emailDays.forEach((day, dayIndex) => {
        const shifts = wardShifts[ward]?.[day.date] || [];
        const dayHasShifts = shifts.length > 0;
        
        if (dayHasShifts) hasShifts = true;
        
        html += `
          <tr style="${dayIndex % 2 === 0 ? 'background-color: #f9f9f9;' : 'background-color: white;'}">
            <td style="padding: 10px; border: 1px solid #ddd; vertical-align: top;">
              <div style="font-weight: bold; color: ${day.isWeekend ? '#e67e22' : '#2c3e50'}">
                ${day.dayName}<br>
                <span style="font-size: 12px; color: #7f8c8d;">${day.date}</span>
              </div>
              ${day.isWeekend ? '<div style="font-size: 11px; color: #e67e22; font-style: italic;">Weekend</div>' : ''}
            </td>
            <td style="padding: 10px; border: 1px solid #ddd;">
        `;
        
        if (dayHasShifts) {
          shifts.forEach((shift, shiftIndex) => {
            const shiftColor = shift.isFilled 
              ? (shift.isWeekend ? '#e67e22' : '#2ecc71') 
              : (shift.isWeekend ? '#f39c12' : '#3498db');
            
            html += `
              <div style="
                background-color: ${shiftColor}10;
                border-left: 4px solid ${shiftColor};
                padding: 8px;
                margin-bottom: 8px;
                border-radius: 4px;
              ">
                <table style="width: 100%;">
                  <tr>
                    <td style="width: 25%;">
                      <strong style="color: ${shiftColor};">${shift.startTime} - ${shift.endTime}</strong>
                    </td>
                    <td style="width: 40%;">
                      <strong>Staff:</strong> ${shift.staffName}
                    </td>
                    <td style="width: 35%;">
                      <strong>Status:</strong> ${shift.isFilled ? 'Assigned' : 'Available'}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3" style="padding-top: 4px;">
                      ${shift.location ? `<strong>Location:</strong> ${shift.location}<br>` : ''}
                      ${shift.isWeekend ? '<span style="color: #e67e22;">• Weekend Shift</span>' : ''}
                      ${!shift.isFilled ? '<span style="color: #3498db; margin-left: 10px;">• Shift Available</span>' : ''}
                    </td>
                  </tr>
                </table>
              </div>
            `;
          });
        } else {
          html += `
            <div style="
              background-color: #f5f5f5;
              padding: 12px;
              text-align: center;
              color: #7f8c8d;
              font-style: italic;
              border-radius: 4px;
            ">
              No shifts scheduled
            </div>
          `;
        }
        
        html += `
            </td>
          </tr>
        `;
      });
      
      if (!hasShifts) {
        html += `
          <tr>
            <td colspan="2" style="padding: 30px; text-align: center; color: #7f8c8d;">
              <div style="font-size: 16px; margin-bottom: 10px;">No shifts scheduled for this week</div>
              <div style="font-size: 14px;">There are no shifts scheduled in ${ward} from ${formatDateRange()}</div>
            </td>
          </tr>
        `;
      }
      
      html += `
              </tbody>
            </table>
          </div>
          
          <div style="margin-top: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
            <h3 style="color: #2c3e50; margin-top: 0;">Ward Summary</h3>
            <ul style="color: #555;">
              <li>Total shifts this week: ${Object.values(wardShifts[ward] || {}).flat().length}</li>
              <li>Filled shifts: ${Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled).length}</li>
              <li>Available shifts: ${Object.values(wardShifts[ward] || {}).flat().filter(s => !s.isFilled).length}</li>
              <li>Weekend shifts: ${Object.values(wardShifts[ward] || {}).flat().filter(s => s.isWeekend).length}</li>
              <li>Days included: ${emailDays.length} (${includeWeekend ? 'Monday to Sunday' : 'Monday to Friday'})</li>
            </ul>
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #7f8c8d; font-size: 12px;">
            <p>
              <strong>Beaumont Hospital Ward Scheduling System</strong><br>
              This schedule was automatically generated. Please do not reply to this email.<br>
              For questions, contact: scheduling@beaumont.ie
            </p>
          </div>
        </div>
      `;
      
      return html;
    };

    // Generate plain text version for email body
    const generateWardSchedulePlainText = (ward) => {
      const emailDays = getEmailDays();
      
      let text = `WARD WEEKLY SCHEDULE\n`;
      text += `====================\n\n`;
      text += `Ward: ${ward}\n`;
      text += `Week: ${formatDateRange()}\n`;
      text += `Generated: ${new Date().toLocaleDateString()}\n`;
      text += `Generated By: ${userInfo?.name || 'Manager'}\n`;
      text += `Includes Weekend: ${includeWeekend ? 'Yes' : 'No'}\n`;
      text += `Days Shown: ${emailDays.length} of ${weekInfo.days.length}\n\n`;
      text += `='.repeat(60)}\n\n`;
      
      let hasShifts = false;
      
      emailDays.forEach(day => {
        const shifts = wardShifts[ward]?.[day.date] || [];
        const dayHasShifts = shifts.length > 0;
        
        if (dayHasShifts) hasShifts = true;
        
        text += `${day.dayName.toUpperCase()} (${day.date})`;
        if (day.isWeekend) text += ' [WEEKEND]';
        text += '\n';
        text += '-'.repeat(40) + '\n';
        
        if (dayHasShifts) {
          shifts.forEach((shift, index) => {
            text += `${index + 1}. ${shift.startTime} - ${shift.endTime}\n`;
            text += `   Staff: ${shift.staffName}\n`;
            text += `   Status: ${shift.isFilled ? 'Assigned' : 'Available'}\n`;
            if (shift.location) text += `   Location: ${shift.location}\n`;
            if (shift.isWeekend) text += `   * Weekend Shift *\n`;
            text += '\n';
          });
        } else {
          text += `No shifts scheduled\n\n`;
        }
        
        text += '\n';
      });
      
      if (!hasShifts) {
        text += `\nNO SHIFTS SCHEDULED\n`;
        text += `There are no shifts scheduled in ${ward} from ${formatDateRange()}\n\n`;
      }
      
      // Add summary
      const totalShifts = Object.values(wardShifts[ward] || {}).flat().length;
      const filledShifts = Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled).length;
      const availableShifts = Object.values(wardShifts[ward] || {}).flat().filter(s => !s.isFilled).length;
      const weekendShifts = Object.values(wardShifts[ward] || {}).flat().filter(s => s.isWeekend).length;
      
      text += `\nWARD SUMMARY:\n`;
      text += `- Total shifts this week: ${totalShifts}\n`;
      text += `- Filled shifts: ${filledShifts}\n`;
      text += `- Available shifts: ${availableShifts}\n`;
      text += `- Weekend shifts: ${weekendShifts}\n`;
      text += `- Days included: ${emailDays.length} (${includeWeekend ? 'Monday to Sunday' : 'Monday to Friday'})\n\n`;
      
      text += `\nIMPORTANT NOTES:\n`;
      text += `- This schedule is subject to change based on operational needs\n`;
      text += `- Please contact ward manager for any schedule changes\n`;
      text += `- Weekend shifts are highlighted\n\n`;
      text += `Beaumont Hospital Ward Scheduling System\n`;
      text += `This schedule was automatically generated.\n`;
      text += `For questions, contact: scheduling@beaumont.ie\n`;
      
      return text;
    };
    
    // Open Outlook with mailto link
    const openOutlookEmail = (ward, subject, body) => {
      // For ward email, we might want to send to multiple recipients
      // For now, we'll just open Outlook with the content
      const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      window.location.href = mailtoLink;
      
      // Check if Outlook opened
      setTimeout(() => {
        if (window.location.href.includes('mailto:')) {
          showSnackbar('Outlook not detected. Using web email instead.', 'info');
          setEmailMethod('web');
          setEmailDialogOpen(true);
        }
      }, 500);
    };

    const handleEmailWardSchedule = (ward) => {
      setSelectedWardForEmail({
        name: ward
      });
      
      // Generate email content
      const defaultSubject = `${ward} Weekly Schedule - Week of ${formatDateRange()}`;
      const plainTextBody = generateWardSchedulePlainText(ward);
      
      setEmailSubject(defaultSubject);
      setEmailContent(plainTextBody);
      
      // Try to open Outlook first
      if (emailMethod === 'outlook') {
        try {
          openOutlookEmail(ward, defaultSubject, plainTextBody);
          
          // Show a message that we're trying Outlook
          showSnackbar('Opening Outlook...', 'info');
          
          // Don't open the dialog if Outlook is being used
          return;
        } catch (error) {
          console.error('Failed to open Outlook:', error);
          showSnackbar('Could not open Outlook. Using web email.', 'warning');
          setEmailMethod('web');
        }
      }
      
      // If Outlook failed or web method is selected, open the dialog
      setEmailDialogOpen(true);
    };

    const handleSendWebEmail = async () => {
      if (!selectedWardForEmail) return;
      
      try {
        // Generate HTML and plain text versions
        const htmlContent = generateWardScheduleHTMLTable(selectedWardForEmail.name);
        const plainTextContent = generateWardSchedulePlainText(selectedWardForEmail.name);
        
        // Construct email data
        const emailData = {
          to: '', // For ward email, you might want to send to ward email list or managers
          subject: emailSubject,
          text: plainTextContent + '\n\n' + emailContent,
          html: htmlContent + '<hr><p><em>Additional Notes:</em></p><p>' + emailContent.replace(/\n/g, '<br>') + '</p>'
        };
        
        // TODO: Call your email API here
        // You might want to get ward email addresses from your backend
        console.log('Ward email data:', emailData);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        showSnackbar(`Ward schedule email prepared for ${selectedWardForEmail.name}`, 'success');
        setEmailDialogOpen(false);
        
      } catch (error) {
        console.error('Failed to send email:', error);
        showSnackbar('Failed to send email', 'error');
      }
    };

    const handleSendEmail = () => {
      if (emailMethod === 'outlook') {
        // Already handled in handleEmailWardSchedule
        setEmailDialogOpen(false);
      } else {
        handleSendWebEmail();
      }
    };

    const handleCancelEmail = () => {
      setEmailDialogOpen(false);
      setSelectedWardForEmail(null);
    };

    const navigateWeek = (direction) => {
      const newDate = new Date(selectedWeek);
      newDate.setDate(newDate.getDate() + (direction * 7));
      setSelectedWeek(newDate);
    };
    
    const goToCurrentWeek = () => {
      setSelectedWeek(new Date());
    };
    
    const formatDateRange = () => {
      const start = new Date(weekInfo.startDate);
      const end = new Date(weekInfo.endDate);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    };

    const showSnackbar = (message, severity = 'success') => {
      setSnackbar({
        open: true,
        message,
        severity
      });
    };
    
    const handleCloseSnackbar = () => {
      setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loadingShifts || loadingStaff || loadingAvailabilities) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        {/* Add Print Styles */}
        <PrintStyles />
        
        {/* Navigation Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
          >
            Dashboard
          </Link>
          <Typography color="text.primary">Ward Weekly View</Typography>
        </Breadcrumbs>
        
        {/* Header with title and buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Ward Weekly Schedule
          </Typography>
          
          {/* Print Button */}
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#673ab7',
              '&:hover': {
                bgcolor: '#5e35b1'
              }
            }}
            className="no-print"
          >
            Print Schedule
          </Button>
        </Box>
        
        {/* Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }} className="no-print">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
            <IconButton onClick={() => navigateWeek(-1)}>
              <ArrowBackIos />
            </IconButton>
            <Typography variant="h6">
              Week of {formatDateRange()}
            </Typography>
            <IconButton onClick={() => navigateWeek(1)}>
              <ArrowForwardIos />
            </IconButton>
            <Button 
              startIcon={<Today />} 
              onClick={goToCurrentWeek}
              variant="outlined"
              size="small"
            >
              Current Week
            </Button>
          </Box>
          
          {/* Filter Controls */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Ward</InputLabel>
            <Select
              value={selectedWardFilter}
              label="Filter by Ward"
              onChange={(e) => setSelectedWardFilter(e.target.value)}
            >
              <MenuItem value="all">All Wards</MenuItem>
              {wards.map(ward => (
                <MenuItem key={ward} value={ward}>{ward}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Job Title</InputLabel>
            <Select
              value={selectedJobTitleFilter}
              label="Filter by Job Title"
              onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
            >
              {jobTitles.map(title => (
                <MenuItem key={title} value={title}>
                  {title === 'all' ? 'All Job Titles' : title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* NEW: Day Filter */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Staff Available by Day</InputLabel>
            <Select
              value={selectedDayFilter}
              label="Staff Available by Day"
              onChange={(e) => setSelectedDayFilter(e.target.value)}
            >
              {dayOptions.map(day => (
                <MenuItem key={day} value={day}>
                  {day === 'all' ? 'All Days' : day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Clear All Filters Button */}
          {(selectedWardFilter !== 'all' || selectedJobTitleFilter !== 'all' || selectedDayFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={clearAllFilters}
              sx={{ height: 40 }}
            >
              Clear Filters
            </Button>
          )}
          
          <Typography variant="body2" color="textSecondary">
            Showing {filteredWards.length} ward{filteredWards.length !== 1 ? 's' : ''} • {filteredStaffList.length} staff ({staffWithAvailabilitiesCount} available this week)
            {selectedDayFilter !== 'all' && ` • Filtered to staff available on ${selectedDayFilter}`}
          </Typography>
        </Box>
        
        {/* Filter Summary */}
        {getFilterSummary() !== 'No filters applied' && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button 
                size="small" 
                color="inherit" 
                onClick={clearAllFilters}
                startIcon={<Clear />}
              >
                Clear
              </Button>
            }
          >
            <Typography variant="body2">
              Filters applied: <strong>{getFilterSummary()}</strong>
            </Typography>
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* Main Ward Schedule */}
          <Grid item xs={9}>
            <Paper elevation={3} sx={{ overflow: 'hidden' }} className="print-page">
              <Box sx={{ minWidth: '1400px' }}>
                {/* Fixed Header Row - OUTSIDE scrollable container */}
                <Grid container sx={fixedHeaderStyle}>
                  {/* Ward column */}
                  <Grid item xs={1.8}>
                    <Box sx={{ 
                      p: 2, 
                      borderRight: 1, 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box>
                        <Typography fontWeight="bold">Ward</Typography>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                          {filteredWards.length} wards
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Actions
                      </Typography>
                    </Box>
                  </Grid>
                  {/* Day columns */}
                  {weekInfo.days.map(day => (
                    <Grid item xs={1.457} key={day.date}>
                      <Box sx={{ 
                        p: 2, 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        bgcolor: day.isWeekend ? 'secondary.main' : 'primary.light',
                        color: 'white',
                        textAlign: 'center'
                      }}>
                        <Typography fontWeight="bold">{day.shortName}</Typography>
                        <Typography variant="body2">
                          {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                        </Typography>
                        {day.isWeekend && (
                          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                            Weekend
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Scrollable Container for Ward Rows */}
                <Box sx={scrollableTableStyle}>
                  {filteredWards.map((ward, index) => (
                    <Grid container key={ward} className="ward-row print-page">
                      {/* Ward Column */}
                      <Grid item xs={1.8}>
                        <Box sx={{ 
                          p: 2, 
                          borderRight: 1, 
                          borderBottom: 1, 
                          borderColor: 'divider',
                          bgcolor: index % 2 === 0 ? 'grey.50' : 'white',
                          minHeight: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          <Box>
                            <Typography fontWeight="bold" variant="h6">
                              {ward}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {wardShifts[ward] ? 
                                Object.values(wardShifts[ward]).flat().length + ' shift(s)' : 
                                'No shifts'
                              }
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              Filled: {Object.values(wardShifts[ward] || {}).flat().filter(s => s.isFilled).length} • 
                              Available: {Object.values(wardShifts[ward] || {}).flat().filter(s => !s.isFilled).length}
                            </Typography>
                          </Box>
                          
                          {/* Email Button */}
                          <Tooltip title={`Email schedule for ${ward}`}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<Email />}
                              onClick={() => handleEmailWardSchedule(ward)}
                              sx={{ 
                                mt: 1,
                                bgcolor: '#1976d2',
                                '&:hover': {
                                  bgcolor: '#1565c0'
                                }
                              }}
                              className="no-print"
                            >
                              Email Ward
                            </Button>
                          </Tooltip>
                        </Box>
                      </Grid>
                      
                      {/* Day Columns */}
                      {weekInfo.days.map(day => (
                        <Grid item xs={1.457} key={`${ward}-${day.date}`}>
                          <Box
                            sx={{
                              p: 1,
                              minHeight: '150px',
                              borderRight: 1,
                              borderBottom: 1,
                              borderColor: 'divider',
                              bgcolor: day.isWeekend ? 'grey.100' : (index % 2 === 0 ? 'grey.50' : 'white'),
                              cursor: 'pointer'
                            }}
                            className="print-page"
                          >
                            {wardShifts[ward]?.[day.date]?.map((shift, idx) => {
                              // Track if we're in a double-click sequence
                              let isDoubleClick = false;
                              let clickTimer = null;
                              
                              const handleCardClick = (e) => {
                                // If this is a double click, set the flag
                                if (e.detail === 2) {
                                  isDoubleClick = true;
                                  // Handle double click for unassign (only for filled shifts)
                                  if (shift.isFilled) {
                                    handleShiftDoubleClick(shift);
                                  }
                                  return;
                                }
                                
                                // Single click - wait to see if it becomes a double-click
                                clickTimer = setTimeout(() => {
                                  if (!isDoubleClick) {
                                    // For available shifts, you could add single-click functionality here
                                    // For now, we'll just handle edit through the icon
                                  }
                                  // Reset for next click sequence
                                  isDoubleClick = false;
                                }, 300);
                              };
                              
                              const handleEditIconClick = (e) => {
                                e.stopPropagation(); // Prevent card click from firing
                                
                                // Clear any pending click timer
                                if (clickTimer) {
                                  clearTimeout(clickTimer);
                                  clickTimer = null;
                                }
                                
                                // Reset double-click flag
                                isDoubleClick = false;
                                
                                // Open edit dialog
                                handleEditShiftClick(shift);
                              };
                              
                              // Get category of staff from shift data
                              const categoryOfStaff = shift.shiftData?.categoryOfStaff || '';
                              
                              return (
                                <Tooltip 
                                  key={`${shift.id}-${idx}`}
                                  title={
                                    <div>
                                      <div><strong>Time:</strong> {shift.startTime} - {shift.endTime}</div>
                                      <div><strong>Status:</strong> {shift.isFilled ? 'Assigned' : 'Available'}</div>
                                      {shift.isFilled ? (
                                        <>
                                          <div><strong>Staff:</strong> {shift.staffName}</div>
                                          {categoryOfStaff && <div><strong>Category:</strong> {categoryOfStaff}</div>}
                                        </>
                                      ) : (
                                        <>
                                          <div><strong>Available Shift</strong></div>
                                          {categoryOfStaff && <div><strong>Category Required:</strong> {categoryOfStaff}</div>}
                                        </>
                                      )}
                                      {shift.location && <div><strong>Location:</strong> {shift.location}</div>}
                                      {shift.isWeekend && <div><strong>Note:</strong> Weekend Shift</div>}
                                      {shift.isFilled ? (
                                        <div style={{ marginTop: '5px', fontStyle: 'italic' }}>
                                          Click edit icon to edit • Double-click card to unassign
                                        </div>
                                      ) : (
                                        <div style={{ marginTop: '5px', fontStyle: 'italic' }}>
                                          Click edit icon to edit • Drag staff to assign
                                        </div>
                                      )}
                                    </div>
                                  }
                                  arrow
                                  className="no-print"
                                >
                                  <Box
                                    sx={{ 
                                      mb: 0.5,
                                      cursor: shift.isFilled ? 'pointer' : 'pointer'
                                    }}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => !shift.isFilled && handleDrop(e, shift)}
                                    onClick={handleCardClick}
                                    className="no-print"
                                  >
                                    <Card sx={{ 
                                      bgcolor: shift.isFilled 
                                        ? (shift.isWeekend ? 'warning.main' : 'success.light')
                                        : (shift.isWeekend ? 'warning.light' : 'info.light'),
                                      color: 'white',
                                      '&:hover': {
                                        boxShadow: shift.isFilled ? 4 : 2,
                                        transform: shift.isFilled ? 'translateY(-2px)' : 'translateY(-1px)',
                                        transition: 'all 0.2s'
                                      },
                                      position: 'relative'
                                    }}>
                                      {/* EDIT ICON - Show for both filled and available shifts */}
                                      <IconButton
                                        size="small"
                                        sx={{
                                          position: 'absolute',
                                          top: 2,
                                          right: 2,
                                          color: 'white',
                                          backgroundColor: 'rgba(0,0,0,0.2)',
                                          padding: '2px',
                                          '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.4)',
                                          }
                                        }}
                                        onClick={handleEditIconClick}
                                        className="no-print"
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                      
                                      <CardContent sx={{ p: 1, pr: 3 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                          {shift.startTime} - {shift.endTime}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                          {shift.staffName}
                                        </Typography>
                                        
                                        {/* Show category of staff for both assigned and available shifts */}
                                        {categoryOfStaff && (
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.65rem', 
                                            display: 'block',
                                            fontStyle: 'italic',
                                            opacity: 0.9
                                          }}>
                                            {categoryOfStaff}
                                          </Typography>
                                        )}
                                        
                                        {shift.isWeekend && (
                                          <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                            Weekend Shift
                                          </Typography>
                                        )}
                                        
                                        {!shift.isFilled && (
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.65rem', 
                                            display: 'block', 
                                            fontStyle: 'italic',
                                            mt: 0.5
                                          }}
                                            className="no-print"
                                          >
                                            Drag staff here
                                          </Typography>
                                        )}
                                        
                                        {shift.isFilled ? (
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.6rem', 
                                            display: 'block', 
                                            opacity: 0.8,
                                            mt: 0.5 
                                          }}
                                            className="no-print"
                                          >
                                            Click edit icon to edit • Double-click to unassign
                                          </Typography>
                                        ) : (
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.6rem', 
                                            display: 'block', 
                                            opacity: 0.8,
                                            mt: 0.5 
                                          }}
                                            className="no-print"
                                          >
                                            Click edit icon to edit • Drag staff to assign
                                          </Typography>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                            
                            {/* Print version of shift cards (no interactivity) */}
                            {wardShifts[ward]?.[day.date]?.map((shift, idx) => (
                              <Box
                                key={`print-${shift.id}-${idx}`}
                                sx={{ 
                                  mb: 0.5,
                                  bgcolor: shift.isFilled 
                                    ? (shift.isWeekend ? '#ff9800' : '#4caf50')
                                    : (shift.isWeekend ? '#ffb74d' : '#2196f3'),
                                  color: 'white',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  display: 'none',
                                  '@media print': {
                                    display: 'block'
                                  }
                                }}
                              >
                                <Box sx={{ fontWeight: 'bold', fontSize: '9px' }}>
                                  {shift.startTime} - {shift.endTime}
                                </Box>
                                <Box sx={{ fontSize: '9px', fontWeight: 'bold' }}>
                                  {shift.staffName}
                                </Box>
                                <Box sx={{ fontSize: '8px' }}>
                                  {shift.isFilled ? 'Assigned' : 'Available'}
                                  {shift.isWeekend ? ' • Weekend' : ''}
                                </Box>
                                {shift.location && (
                                  <Box sx={{ fontSize: '8px' }}>
                                    {shift.location}
                                  </Box>
                                )}
                              </Box>
                            ))}
                            
                            {/* Empty state */}
                            {(!wardShifts[ward]?.[day.date] || wardShifts[ward][day.date].length === 0) && (
                              <Box
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                  fontSize: '0.8rem',
                                  textAlign: 'center',
                                  p: 1,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                    border: '1px dashed',
                                    borderColor: 'primary.main',
                                  }
                                }}
                                onClick={(e) => {
                                  if (e.detail === 2) { // Double-click
                                    handleCreateShiftClick(ward, day);
                                  }
                                }}
                                className="no-print"
                              >
                                Double-click to create shift
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ))}
                  
                  {/* Empty state for no wards */}
                  {filteredWards.length === 0 && (
                    <Grid container>
                      <Grid item xs={12}>
                        <Box sx={{ 
                          p: 4, 
                          textAlign: 'center',
                          borderBottom: 1,
                          borderColor: 'divider'
                        }}>
                          <Typography variant="body1" color="textSecondary">
                            No wards found for the selected week
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          {/* Staff Panel */}
          <Grid item xs={3} className="no-print">
            <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Available Staff ({filteredStaffList.length})
                </Typography>
                
                {/* Add sick leave summary */}
                {filteredStaffList.some(staff => {
                  const availabilities = getStaffAvailabilities(staff._id);
                  return availabilities.some(a => a.isSickLeave);
                }) && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                    <Typography variant="caption">
                      ⚠️ Some staff have sick leave marked this week (shown in red)
                    </Typography>
                  </Alert>
                )}

                {/* Filter badges */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  {selectedJobTitleFilter !== 'all' && (
                    <Chip 
                      label={`Job: ${selectedJobTitleFilter}`}
                      size="small"
                      color="primary"
                      onDelete={() => setSelectedJobTitleFilter('all')}
                    />
                  )}
                  {selectedDayFilter !== 'all' && (
                    <Chip 
                      label={`Day: ${selectedDayFilter}`}
                      size="small"
                      color="secondary"
                      onDelete={() => setSelectedDayFilter('all')}
                    />
                  )}
                </Box>
                
                <Typography variant="body2" color="textSecondary" paragraph>
                  Drag to assign to shifts • {staffWithAvailabilitiesCount} staff available this week
                  {selectedDayFilter !== 'all' && ` (filtered to ${selectedDayFilter})`}
                </Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {selectedDayFilter === 'all' 
                    ? 'Staff are sorted with those available this week first, then alphabetically'
                    : `Showing only staff available on ${selectedDayFilter}`
                  }
                </Typography>
              </Alert>
              
              <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
                {filteredStaffList.length === 0 ? (
                  <Alert severity={staffList.length === 0 ? "info" : "warning"}>
                    {staffList.length === 0 
                      ? "No staff available" 
                      : `No staff match the selected ${selectedJobTitleFilter !== 'all' ? 'job title ' : ''}${selectedDayFilter !== 'all' ? 'day ' : ''}filter`}
                    {(selectedJobTitleFilter !== 'all' || selectedDayFilter !== 'all') && (
                      <Button 
                        size="small" 
                        onClick={clearAllFilters}
                        sx={{ ml: 1 }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Alert>
                ) : (
                  <>
                    {staffWithAvailabilitiesCount > 0 && staffWithAvailabilitiesCount < filteredStaffList.length && (
                      <Box sx={{ mb: 2 }}>
                        <Divider sx={{ mb: 1 }}>
                          <Chip 
                            label={`${staffWithAvailabilitiesCount} with availability`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Divider>
                      </Box>
                    )}
                    
                    {filteredStaffList.map((staff) => {
                      const staffAvailabilities = getStaffAvailabilities(staff._id);
                      const hasAvailability = staffAvailabilities.length > 0;
                      
                      return (
                        <Card
                          key={staff._id}
                          draggable={true}
                          onDragStart={(e) => {
                            console.log('🖱️ Card drag start for:', staff.name);
                            
                            // Store the staff data for the drag
                            e.dataTransfer.setData('text/plain', JSON.stringify(staff));
                            setDraggedStaff(staff);
                            
                            // Check for unavailability on ANY date
                            const staffAvailabilities = getStaffAvailabilities(staff._id);
                            const hasAnyUnavailability = staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave);
                            
                            if (hasAnyUnavailability) {
                              console.log('⚠️ Staff has unavailability on some dates');
                              // Add warning to drag data
                              const unavailableDates = staffAvailabilities
                                .filter(a => a.isUnavailable || a.isSickLeave)
                                .map(a => new Date(a.startDateTime).toISOString().split('T')[0]);
                              
                              e.dataTransfer.setData('unavailableDates', JSON.stringify(unavailableDates));
                            }
                            
                            // Add debug info
                            console.log('📋 Staff being dragged:', {
                              name: staff.name,
                              id: staff._id,
                              jobtitle: staff.jobtitle,
                              hasAnyUnavailability: hasAnyUnavailability
                            });
                          }}
                          sx={{ 
                            mb: 2, 
                            cursor: 'grab',
                            borderLeft: hasAvailability ? '4px solid #4caf50' : '4px solid transparent',
                            // Add red border if ANY availability is marked as unavailable
                            borderTop: staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave) ? '2px solid #f44336' : 'none',
                            opacity: staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave) ? 0.7 : 1,
                            '&:hover': { 
                              transform: 'translateY(-2px)',
                              transition: 'transform 0.2s',
                              boxShadow: 3
                            }
                          }}
                          className="no-print"
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {staff.name}
                                </Typography>
                                <Typography variant="body2">
                                  {staff.jobtitle}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                  {staff.email}
                                </Typography>
                              </Box>
                              
                              {hasAvailability && (
                                <Tooltip title={`${staffAvailabilities.length} availability${staffAvailabilities.length !== 1 ? 's' : ''} this week`}>
                                  <Chip 
                                    icon={<EventAvailable fontSize="small" />}
                                    label={`${staffAvailabilities.length}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                </Tooltip>
                              )}

                              {/* Add unavailability indicator badge */}
                              {staffAvailabilities.some(a => a.isUnavailable || a.isSickLeave) && (
                                <Tooltip title="Staff has unavailability marked this week">
                                  <Chip 
                                    label="Unavailable" 
                                    size="small" 
                                    color="error"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem' }}
                                  />
                                </Tooltip>
                              )}

                              {/* Add sick leave indicator badge */}
                              {staffAvailabilities.some(a => a.isSickLeave) && (
                                <Tooltip title="Staff has sick leave marked this week">
                                  <Chip 
                                    label="Sick Leave" 
                                    size="small" 
                                    color="error"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem' }}
                                  />
                                </Tooltip>
                              )}

                            </Box>
                            
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={staff.speciality || 'General'} 
                                size="small" 
                                variant="outlined"
                              />
                              {staff.isLocum && (
                                <Chip 
                                  label="Locum" 
                                  size="small" 
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {staff.isBank && (
                                <Chip 
                                  label="Bank" 
                                  size="small" 
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            
                            {hasAvailability && (
                              <Box sx={{ mt: 2 }} className="availability-section">
                                <Divider sx={{ mb: 1 }} />
                                <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                                  Available This Week:
                                </Typography>
                                <Box sx={{ maxHeight: '150px', overflow: 'auto' }}>
                                  {staffAvailabilities.map((availability, index) => (
                                    <Box 
                                      key={availability._id || index}
                                      sx={{ 
                                        mb: 0.5,
                                        p: 0.5,
                                        bgcolor: (availability.isUnavailable || availability.isSickLeave) ? 'error.50' : 'primary.50',
                                        borderRadius: '4px',
                                        borderLeft: '3px solid',
                                        borderColor: (availability.isUnavailable || availability.isSickLeave) ? 'error.main' : 'primary.main'
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                        {formatAvailabilityDisplay(availability)}
                                        {(availability.isUnavailable || availability.isSickLeave) && (
                                          <Chip 
                                            label="Unavailable" 
                                            size="small" 
                                            sx={{ 
                                              ml: 1,
                                              height: 14,
                                              fontSize: '0.55rem',
                                              bgcolor: 'error.main',
                                              color: 'white'
                                            }} 
                                          />
                                        )}
                                      </Typography>
                                      {availability.comments && (
                                        <Typography variant="caption" sx={{ 
                                          fontSize: '0.65rem', 
                                          color: 'text.secondary',
                                          fontStyle: 'italic',
                                          display: 'block'
                                        }}>
                                          {availability.comments}
                                        </Typography>
                                      )}
                                      {(availability.isUnavailable || availability.isSickLeave) && (availability.unavailabilityReason || availability.sicknessReason) && (
                                        <Typography variant="caption" sx={{ 
                                          fontSize: '0.65rem', 
                                          color: 'error.main',
                                          fontStyle: 'italic',
                                          display: 'block'
                                        }}>
                                          Reason: {availability.unavailabilityReason || availability.sicknessReason}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                              Drag to assign
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
        
               
        {/* Create New Shift Dialog */}
  <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="lg" fullWidth className="no-print">
    <DialogTitle>Create New Shift</DialogTitle>
    <DialogContent>
      {newShiftInfo.ward && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Creating new shift for: <strong>{newShiftInfo.ward}</strong>
              <br />
              Date: <strong>{newShiftInfo.dayInfo?.dayName}, {new Date(newShiftInfo.dayDate).toLocaleDateString()}</strong>
            </Typography>
          </Alert>
          
          {/* Tabs for Basic Info and Schedule */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: 2 }}
            >
              <Tab label="Basic Information" value="basic" />
              <Tab label="Schedule" value="schedule" />
            </Tabs>
          </Box>
          
          {/* Tab Content - Reuse the same form as edit dialog */}
          {activeTab === 'basic' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Top Row: Hospital Location, Category, Reason */}
              <Grid item xs={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Hospital Location</InputLabel>
                  <Select
                    name="hospitalLocationId"
                    value={editForm.hospitalLocationId || ''}
                    label="Hospital Location"
                    onChange={handleEditFormChange}
                    size="small"
                    required
                  >
                    <MenuItem value="">Select Hospital Location...</MenuItem>
                    {hospitalLocations?.map(location => (
                      <MenuItem key={location._id} value={location._id}>
                        {location.wardDepartmentArea} ({location.department})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category of Staff</InputLabel>
                  <Select
                    name="categoryOfStaff"
                    value={editForm.categoryOfStaff || ''}
                    label="Category of Staff"
                    onChange={handleEditFormChange}
                    size="small"
                    required
                  >
                    <MenuItem value="">Select Category...</MenuItem>
                    {CATEGORY_OF_STAFF.map(c => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Reason for Request</InputLabel>
                  <Select
                    name="reasonForRequest"
                    value={editForm.reasonForRequest || ''}
                    label="Reason for Request"
                    onChange={handleEditFormChange}
                    size="small"
                    required
                  >
                    <MenuItem value="">Select Reason...</MenuItem>
                    {REASON_FOR_REQUEST.map(r => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Middle Row: Reason Details */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for Request Details"
                  name="reasonForRequestDetails"
                  value={editForm.reasonForRequestDetails || ''}
                  onChange={handleEditFormChange}
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder="Provide details about why this shift is needed..."
                  required
                />
              </Grid>
              
              {/* Options Row */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Options
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editForm.escort || false}
                            onChange={(e) => setEditForm(prev => ({ ...prev, escort: e.target.checked }))}
                            name="escort"
                          />
                        }
                        label="Escort Required"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editForm.special || false}
                            onChange={(e) => setEditForm(prev => ({ ...prev, special: e.target.checked }))}
                            name="special"
                          />
                        }
                        label="Is this a Special"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editForm.isOvertime || false}
                            onChange={(e) => setEditForm(prev => ({ ...prev, isOvertime: e.target.checked }))}
                            name="isOvertime"
                          />
                        }
                        label="Overtime Shift"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editForm.isActive !== false}
                            onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                            name="isActive"
                          />
                        }
                        label="Active Shift"
                      />
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                        Uncheck to create an inactive shift
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        label="ECC Approval Number"
                        name="eccNumber"
                        value={editForm.eccNumber || ''}
                        onChange={handleEditFormChange}
                        size="small"
                        placeholder="Enter ECC number..."
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Special Requirements and Comments */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Special Requirements"
                  name="specialRequirements"
                  value={editForm.specialRequirements || ''}
                  onChange={handleEditFormChange}
                  margin="normal"
                  multiline
                  rows={4}
                  placeholder="Any special equipment, training, or requirements..."
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Comments"
                  name="comments"
                  value={editForm.comments || ''}
                  onChange={handleEditFormChange}
                  margin="normal"
                  multiline
                  rows={4}
                  placeholder="Additional notes or comments..."
                />
              </Grid>
            </Grid>
          )}
          
          {activeTab === 'schedule' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Shift Time Presets */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Shift Time Preset
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                      <Grid item xs={6} md={4} key={key}>
                        <Paper
                          elevation={editForm.selectedPreset === key ? 2 : 0}
                          sx={{
                            p: 1,
                            cursor: 'pointer',
                            bgcolor: editForm.selectedPreset === key ? 'primary.main' : 'grey.100',
                            color: editForm.selectedPreset === key ? 'white' : 'inherit',
                            '&:hover': {
                              bgcolor: editForm.selectedPreset === key ? 'primary.dark' : 'grey.200',
                            }
                          }}
                          onClick={() => handlePresetSelect(key)}
                        >
                          <Typography variant="body2">
                            {preset.label}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Date and Time Fields */}
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  name="startDate"
                  value={editForm.startDate || ''}
                  onChange={handleEditFormChange}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  size="small"
                  required
                />
              </Grid>
              
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="End Date (Same Day)"
                  type="date"
                  name="endDate"
                  value={editForm.endDate || ''}
                  onChange={handleEditFormChange}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  size="small"
                  helperText="Same day unless overnight shift"
                  required
                />
              </Grid>
              
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  name="startTime"
                  value={editForm.startTime || ''}
                  onChange={handleEditFormChange}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  size="small"
                  required
                />
              </Grid>
              
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  name="endTime"
                  value={editForm.endTime || ''}
                  onChange={handleEditFormChange}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  size="small"
                  required
                />
              </Grid>
              
              {/* Shift Duration Display */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Shift Duration:</strong> {calculateShiftDuration()}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
      <Button 
        onClick={handleCreateShift} 
        variant="contained" 
        color="primary"
        startIcon={<Edit />}
        disabled={!editForm.hospitalLocationId || !editForm.categoryOfStaff || !editForm.reasonForRequest}
      >
        Create Shift
      </Button>
    </DialogActions>
  </Dialog>


        {/* Release Shift Confirmation Dialog */}
        <Dialog open={releaseDialogOpen} onClose={handleCancelRelease} maxWidth="sm" fullWidth className="no-print">
          <DialogTitle>Unassign Staff from Shift</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Are you sure you want to unassign this staff member from the shift?
            </Typography>
            {shiftToRelease && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Staff:</strong> {shiftToRelease.staffName}
                </Typography>
                <Typography variant="body2">
                  <strong>Ward:</strong> {shiftToRelease.ward}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {shiftToRelease.shiftTime}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(shiftToRelease.dayDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              This will make the shift available for assignment to another staff member.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelRelease}>Cancel</Button>
            <Button 
              onClick={handleReleaseShift} 
              variant="contained" 
              color="warning"
              startIcon={<Delete />}
            >
              Unassign Staff
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Enhanced Edit Shift Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="lg" fullWidth className="no-print">
          <DialogTitle>Edit Shift Details</DialogTitle>
          <DialogContent>
            {shiftToEdit && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Editing shift in: <strong>{shiftToEdit.ward}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Staff: <strong>{shiftToEdit.staffName}</strong>
                </Typography>
                
                {/* Tabs for Basic Info and Schedule */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{ mb: 2 }}
                  >
                    <Tab label="Basic Information" value="basic" />
                    <Tab label="Schedule" value="schedule" />
                  </Tabs>
                </Box>
                
                {/* Tab Content */}
                {activeTab === 'basic' && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Top Row: Hospital Location, Category, Reason */}
                    <Grid item xs={4}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Hospital Location</InputLabel>
                        <Select
                          name="hospitalLocationId"
                          value={editForm.hospitalLocationId || ''}
                          label="Hospital Location"
                          onChange={handleEditFormChange}
                          size="small"
                        >
                          {hospitalLocations?.map(location => (
                            <MenuItem key={location._id} value={location._id}>
                              {location.wardDepartmentArea} ({location.department})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Category of Staff</InputLabel>
                        <Select
                          name="categoryOfStaff"
                          value={editForm.categoryOfStaff || ''}
                          label="Category of Staff"
                          onChange={handleEditFormChange}
                          size="small"
                        >
                          <MenuItem value="">Select Category...</MenuItem>
                          {CATEGORY_OF_STAFF.map(c => (
                            <MenuItem key={c.value} value={c.value}>
                              {c.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Reason for Request</InputLabel>
                        <Select
                          name="reasonForRequest"
                          value={editForm.reasonForRequest || ''}
                          label="Reason for Request"
                          onChange={handleEditFormChange}
                          size="small"
                        >
                          <MenuItem value="">Select Reason...</MenuItem>
                          {REASON_FOR_REQUEST.map(r => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Middle Row: Reason Details */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Reason for Request Details"
                        name="reasonForRequestDetails"
                        value={editForm.reasonForRequestDetails || ''}
                        onChange={handleEditFormChange}
                        margin="normal"
                        multiline
                        rows={3}
                        placeholder="Provide details about why this shift is needed..."
                      />
                    </Grid>
                    
                    {/* Options Row */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Options
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={editForm.escort || false}
                                  onChange={(e) => setEditForm(prev => ({ ...prev, escort: e.target.checked }))}
                                  name="escort"
                                />
                              }
                              label="Escort Required"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={editForm.special || false}
                                  onChange={(e) => setEditForm(prev => ({ ...prev, special: e.target.checked }))}
                                  name="special"
                                />
                              }
                              label="Is this a Special"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={editForm.isOvertime || false}
                                  onChange={(e) => setEditForm(prev => ({ ...prev, isOvertime: e.target.checked }))}
                                  name="isOvertime"
                                />
                              }
                              label="Overtime Shift"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={editForm.isActive !== false}
                                  onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                  name="isActive"
                                />
                              }
                              label="Active Shift"
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                              Uncheck to "delete" the shift (sets isActive=false)
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <TextField
                              fullWidth
                              label="ECC Approval Number"
                              name="eccNumber"
                              value={editForm.eccNumber || ''}
                              onChange={handleEditFormChange}
                              size="small"
                              placeholder="Enter ECC number..."
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                    
                    {/* Special Requirements and Comments */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Special Requirements"
                        name="specialRequirements"
                        value={editForm.specialRequirements || ''}
                        onChange={handleEditFormChange}
                        margin="normal"
                        multiline
                        rows={4}
                        placeholder="Any special equipment, training, or requirements..."
                      />
                    </Grid>
                    
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Comments"
                        name="comments"
                        value={editForm.comments || ''}
                        onChange={handleEditFormChange}
                        margin="normal"
                        multiline
                        rows={4}
                        placeholder="Additional notes or comments..."
                      />
                    </Grid>
                  </Grid>
                )}
                
                {activeTab === 'schedule' && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Shift Time Presets */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Shift Time Preset
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={1}>
                          {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                            <Grid item xs={6} md={4} key={key}>
                              <Paper
                                elevation={editForm.selectedPreset === key ? 2 : 0}
                                sx={{
                                  p: 1,
                                  cursor: 'pointer',
                                  bgcolor: editForm.selectedPreset === key ? 'primary.main' : 'grey.100',
                                  color: editForm.selectedPreset === key ? 'white' : 'inherit',
                                  '&:hover': {
                                    bgcolor: editForm.selectedPreset === key ? 'primary.dark' : 'grey.200',
                                  }
                                }}
                                onClick={() => handlePresetSelect(key)}
                              >
                                <Typography variant="body2">
                                  {preset.label}
                                </Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                    
                    {/* Date and Time Fields */}
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        name="startDate"
                        value={editForm.startDate || ''}
                        onChange={handleEditFormChange}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                        size="small"
                      />
                    </Grid>
                    
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="End Date (Same Day)"
                        type="date"
                        name="endDate"
                        value={editForm.endDate || ''}
                        onChange={handleEditFormChange}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                        size="small"
                        helperText="Same day unless overnight shift"
                      />
                    </Grid>
                    
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Start Time"
                        type="time"
                        name="startTime"
                        value={editForm.startTime || ''}
                        onChange={handleEditFormChange}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                        size="small"
                      />
                    </Grid>
                    
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="End Time"
                        type="time"
                        name="endTime"
                        value={editForm.endTime || ''}
                        onChange={handleEditFormChange}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                        size="small"
                      />
                    </Grid>
                    
                    {/* Shift Duration Display */}
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Shift Duration:</strong> {calculateShiftDuration()}
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEdit}>Cancel</Button>
            <Button 
              onClick={handleUpdateShift} 
              variant="contained" 
              color="primary"
              startIcon={<Edit />}
            >
              Update Shift
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Email Dialog (for web email fallback) */}
        <Dialog open={emailDialogOpen} onClose={handleCancelEmail} maxWidth="md" fullWidth className="no-print">
          <DialogTitle>Email Ward Schedule</DialogTitle>
          <DialogContent>
            {selectedWardForEmail && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Ward:</strong> {selectedWardForEmail.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Week:</strong> ${formatDateRange()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Generated By:</strong> ${userInfo?.name || 'Manager'}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Email Method:
                    </Typography>
                    <ToggleButtonGroup
                      value={emailMethod}
                      exclusive
                      onChange={(e, newMethod) => newMethod && setEmailMethod(newMethod)}
                      size="small"
                    >
                      <ToggleButton value="outlook">
                        <DesktopWindows sx={{ mr: 1 }} />
                        Outlook
                      </ToggleButton>
                      <ToggleButton value="web">
                        <Language sx={{ mr: 1 }} />
                        Web Email
                      </ToggleButton>
                    </ToggleButtonGroup>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeWeekend}
                          onChange={(e) => setIncludeWeekend(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Include Weekend Days"
                      sx={{ mt: 1, display: 'block' }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      {includeWeekend ? 'Saturday and Sunday will be included' : 'Only Monday to Friday will be included'}
                    </Typography>
                  </Box>
                </Box>
                
                {emailMethod === 'web' && (
                  <>
                    <TextField
                      fullWidth
                      label="Email Subject"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      margin="normal"
                    />
                    
                    <TextField
                      fullWidth
                      label="Additional Notes (optional)"
                      value={emailContent}
                      onChange={(e) => setEmailContent(e.target.value)}
                      margin="normal"
                      multiline
                      rows={4}
                      helperText="Add any additional notes or instructions for the ward"
                    />
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Schedule Preview (showing {includeWeekend ? 'all 7 days' : '5 weekdays'}):
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9', maxHeight: '200px', overflow: 'auto' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {generateWardSchedulePlainText(selectedWardForEmail.name).slice(0, 500)}...
                      </Typography>
                    </Paper>
                  </>
                )}
                
                {emailMethod === 'outlook' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Clicking "Send Email" will open Microsoft Outlook with the ward schedule pre-populated.
                      If Outlook is not installed, the system will automatically fall back to web email.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                      Weekend days are {includeWeekend ? 'INCLUDED' : 'NOT INCLUDED'} in this email.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEmail}>Cancel</Button>
            <Button 
              onClick={handleSendEmail} 
              variant="contained" 
              color="primary"
              startIcon={emailMethod === 'outlook' ? <DesktopWindows /> : <Send />}
            >
              {emailMethod === 'outlook' ? 'Open in Outlook' : 'Send Web Email'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          className="no-print"
        />
      </Box>
    );
  };

  export default WardWeeklyView;